/**
 * Pure mapping for the server-side Reports export (KAN-548 / KAN-614).
 *
 * `POST /api/admin/reports/:aba/export` answers three ways and the request is
 * made with `responseType: "blob"` so the file bytes survive intact — which
 * means the JSON variants (202 job, error bodies) arrive as Blobs too. This
 * module turns an axios response / error into a plain result the delegate can
 * switch on, without touching React or axios itself (so it is unit-testable).
 *
 * Lives in `utils/` because both the data layer (`data.controller.js`) and the
 * reports feature (`reports-export.controller.js`) read it.
 */

export const EXPORT_RESULT = Object.freeze({
    FILE: 'file',
    EMPTY: 'empty',
    JOB: 'job',
});

/** Terminal job statuses as the service reports them. */
export const EXPORT_JOB_STATUS = Object.freeze({
    QUEUED: 'queued',
    PROCESSING: 'processing',
    COMPLETED: 'completed',
    EMPTY: 'empty',
    FAILED: 'failed',
});

export const EXPORT_JOB_TERMINAL_STATUSES = Object.freeze([
    EXPORT_JOB_STATUS.COMPLETED,
    EXPORT_JOB_STATUS.EMPTY,
    EXPORT_JOB_STATUS.FAILED,
]);

/**
 * `attachment; filename="clockout-users-all-20260909-0905.xlsx"` → the name.
 * @param {string|null|undefined} header
 * @returns {string|null}
 */
export function parseContentDispositionFileName(header) {
    if (!header || typeof header !== 'string') return null;
    const quoted = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
    if (!quoted) return null;
    try {
        return decodeURIComponent(quoted[1].trim());
    } catch (_e) {
        return quoted[1].trim();
    }
}

/**
 * Read JSON out of whatever axios handed back for a non-file response: a
 * Blob (responseType blob), a string, or an already-parsed object.
 * @param {*} data
 * @returns {Promise<Object|null>}
 */
export async function readJsonBody(data) {
    if (data === null || data === undefined) return null;
    if (typeof data === 'object' && typeof data.text === 'function') {
        try {
            const text = await data.text();
            return text ? JSON.parse(text) : null;
        } catch (_e) {
            return null;
        }
    }
    if (typeof data === 'string') {
        try {
            return data ? JSON.parse(data) : null;
        } catch (_e) {
            return null;
        }
    }
    return typeof data === 'object' ? data : null;
}

function header(response, name) {
    const headers = response?.headers;
    if (!headers) return null;
    if (typeof headers.get === 'function') {
        const v = headers.get(name);
        if (v !== undefined && v !== null) return v;
    }
    return headers[name] ?? headers[name.toLowerCase()] ?? null;
}

/**
 * Map the export response to a result the UI switches on.
 *
 * @param {{ status: number, data: *, headers?: Object }} response axios response
 * @param {{ fallbackFileName?: string }} [options]
 * @returns {Promise<
 *   | { kind: 'empty' }
 *   | { kind: 'job', jobId: string, rowCount: number|null }
 *   | { kind: 'file', blob: Blob, fileName: string, rowCount: number|null }
 * >}
 */
export async function mapExportResponse(response, { fallbackFileName = 'clockout-export.xlsx' } = {}) {
    const status = Number(response?.status);

    if (status === 204) {
        return { kind: EXPORT_RESULT.EMPTY };
    }

    if (status === 202) {
        const body = (await readJsonBody(response?.data)) || {};
        return {
            kind: EXPORT_RESULT.JOB,
            jobId: body.job_id ?? null,
            rowCount: body.row_count != null && Number.isFinite(Number(body.row_count)) ? Number(body.row_count) : null,
        };
    }

    const rowCountHeader = header(response, 'x-export-row-count');
    const rowCount = rowCountHeader !== null && Number.isFinite(Number(rowCountHeader)) ? Number(rowCountHeader) : null;
    const fileName =
        header(response, 'x-export-file-name') ||
        parseContentDispositionFileName(header(response, 'content-disposition')) ||
        fallbackFileName;

    return { kind: EXPORT_RESULT.FILE, blob: response?.data, fileName, rowCount };
}

/**
 * Human message for a failed export request. Error bodies are Blobs under
 * `responseType: "blob"`, so the service's `{ error }` has to be read back
 * out before it can be shown.
 *
 * @param {*} error axios error
 * @returns {Promise<string>}
 */
export async function mapExportError(error) {
    const status = error?.response?.status;
    const body = await readJsonBody(error?.response?.data);
    if (body?.error) return String(body.error);
    if (status === 403) return 'You do not have permission to export this report.';
    if (status === 422) return 'This export is too large. Narrow the filters or date range and try again.';
    if (status === 429) return 'Too many export requests. Please wait a moment and try again.';
    return 'The export could not be generated. Please try again.';
}

/**
 * Classify a failed poll of `GET …/export/:job_id`. A 4xx other than 429 is
 * permanent (the job is not this admin's, is gone, or the tab scope was
 * revoked) and is reported as a failed job so the poller stops; anything else
 * (network, 429, 5xx) returns `null` so the next tick retries.
 *
 * @param {*} error axios error
 * @returns {{ status: string, error: string }|null}
 */
export function mapExportJobError(error) {
    const status = Number(error?.response?.status);
    if (!(status >= 400 && status < 500) || status === 429) return null;
    const body = error?.response?.data;
    const detail = body && typeof body === 'object' && body.error ? String(body.error) : null;
    if (detail) return { status: EXPORT_JOB_STATUS.FAILED, error: detail };
    if (status === 404) return { status: EXPORT_JOB_STATUS.FAILED, error: 'This export is no longer available.' };
    if (status === 403)
        return { status: EXPORT_JOB_STATUS.FAILED, error: 'You do not have permission to download this export.' };
    return { status: EXPORT_JOB_STATUS.FAILED, error: 'The export could not be retrieved.' };
}
